import { useEffect, useRef, useState } from "react";
import { Avatar } from "@themes/pollinator/ui/Avatar";
import { Loader } from "@themes/pollinator/ui/Loader";

/**
 * How many chains of changes run side by side. Each keeps its own time, so
 * the field has more going on at once than one could give it without
 * hurrying, and their beats never line up into a rhythm.
 */
export const CLOCKS = 2;
/**
 * How long one clock waits after its change starts before starting its next,
 * drawn afresh each time from this range, so neither keeps a beat the eye
 * can predict. Two clocks at this pace, with the moment each face takes to
 * load and be drawn, start a little under two changes a second between them,
 * so three places are part way through a change most of the time.
 */
export const WAIT_MS = { shortest: 600, longest: 1400 };
/**
 * The least time between any two changes beginning, whichever clocks they
 * belong to. Two faces starting to fade together read as a jolt rather than
 * as the field breathing, so a clock whose turn falls too soon after the
 * other's waits out the difference.
 */
export const SPACING_MS = 400;
/**
 * How long one face takes to give way to another. The outgoing face eases out
 * over the first seven tenths of it and the incoming one eases in over the
 * last seven tenths, so the two overlap in the middle and the place dims
 * along a smooth curve to about a third of its strength and back, with no
 * corner anywhere. Fading one face to nothing and the other up after it, each
 * at an even pace, turned sharply at nothing and read as rushing into it and
 * out again; fading both at once over the whole change dimmed the place too
 * little to notice.
 */
export const FADE_MS = 1600;
/** One face's fade within a change, and the moment the incoming one starts. */
export const FACE_FADE_MS = 1120;
export const INCOMING_DELAY_MS = FADE_MS - FACE_FADE_MS;
/**
 * The same, as classes, since Tailwind builds only the classes it can read.
 * The easing is a symmetric ease in and out, which is what rounds the dip.
 */
const FADE = "duration-[1120ms] ease-[cubic-bezier(0.33,0,0.67,1)]";
const INCOMING_DELAY = "delay-[480ms]";
/** How long an incoming face may take to load before its turn is given up. */
const LOAD_PATIENCE_MS = 4000;

const GAP_PX = 12;
const FACE_PX = { compact: 36, desktop: 48 };
const ROWS = { phone: 5, wider: 3 };
/** The design's desktop field, used until the real width has been measured. */
const DEFAULT_LAYOUT = { perRow: 19, rows: ROWS.wider };

export type Props = {
  /** Thumbnail addresses, already in the order they should first appear */
  photos: string[];
  /** Whether the list is still on its way; the field holds its place meanwhile */
  isLoading?: boolean;
  /** Whether faces outside the grid take turns in it; off for reduced motion */
  isRotating?: boolean;
  className?: string;
};

type Layout = { perRow: number; rows: number };

/** How many faces fill the field exactly, never leaving a row part-filled. */
export function visibleCount(available: number, { perRow, rows }: Layout): number {
  if (available < perRow) return available;
  return Math.min(rows, Math.floor(available / perRow)) * perRow;
}

function measure(element: HTMLElement | null): Layout {
  const width = element?.clientWidth ?? 0;
  if (!width || typeof window.matchMedia !== "function") return DEFAULT_LAYOUT;
  const face = window.matchMedia("(min-width: 1024px)").matches
    ? FACE_PX.desktop
    : FACE_PX.compact;
  const rows = window.matchMedia("(min-width: 768px)").matches ? ROWS.wider : ROWS.phone;
  return { perRow: Math.max(1, Math.floor((width + GAP_PX) / (face + GAP_PX))), rows };
}

type FaceProps = {
  src: string;
  isOnTop: boolean;
  /** Fading out, because the face arriving over it has loaded */
  isLeaving?: boolean;
  onShown?: () => void;
};

/**
 * One face, invisible until its image has arrived and then faded up. One
 * arriving over another fades up as the one beneath fades out.
 */
function Face({ src, isLeaving = false, isOnTop, onShown }: FaceProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  // A browser fades an element only from a strength it has already drawn it
  // at. An image it has cached can report itself loaded before the face has
  // been drawn invisible even once, and the face then appeared at full
  // strength with no fade at all. It is shown two frames after loading, by
  // which time its invisible state has been drawn.
  const frames = useRef<number[]>([]);
  const hasLoaded = useRef(false);
  const element = useRef<HTMLDivElement>(null);
  const loaded = () => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    frames.current.push(
      requestAnimationFrame(() => {
        frames.current.push(
          requestAnimationFrame(() => {
            setIsLoaded(true);
            onShown?.();
          })
        );
      })
    );
  };
  // An image the browser already holds, as the next face's is once it has
  // been asked for ahead of time, can be complete before its element is
  // listening, and then reports no load at all. One already complete when the
  // face is put on the page is taken as loaded.
  useEffect(() => {
    const pending = frames.current;
    const image = element.current?.querySelector("img");
    if (image?.complete && image.naturalWidth > 0) loaded();
    // Taken off the page, a face forgets it had loaded, so one put back, as
    // development's strict mode does at once, looks again rather than
    // waiting for a load already over.
    return () => {
      pending.forEach(cancelAnimationFrame);
      pending.length = 0;
      hasLoaded.current = false;
    };
    // Once, as the face is put on the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Avatar
      ref={element}
      image={src}
      alt=""
      onImageLoad={loaded}
      // The incoming face starts after the outgoing one, so their fades
      // overlap in the middle of the change.
      className={`absolute inset-0 !h-full !w-full bg-transparent transition-opacity ${FADE} ${
        isOnTop ? INCOMING_DELAY : ""
      } ${isLoaded && !isLeaving ? "opacity-100" : "opacity-0"}`}
      data-testid={isOnTop ? "marketing-avatar-grid-incoming" : "marketing-avatar-grid-face"}
    />
  );
}

type PlaceProps = { layers: string[]; onArrived: (src: string) => void };

/**
 * One place in the field: the face showing, and mid-change the face arriving
 * over it. The face showing begins to fade only once the arriving one has
 * loaded, so a change never starts with nothing ready to follow it.
 */
function Place({ layers, onArrived }: PlaceProps) {
  const [arrived, setArrived] = useState<string>();
  const incoming = layers.length > 1 ? layers[layers.length - 1] : undefined;
  // Forgotten once a change is over, or a face coming back to this place
  // later would set the one it replaces fading before it had loaded.
  useEffect(() => {
    if (!incoming) setArrived(undefined);
  }, [incoming]);
  return (
    <div
      // The hairline is the design's: black at 8%, round each face.
      className="relative h-9 w-9 flex-none rounded-full ring-1 ring-inset ring-black/[0.08] lg:h-12 lg:w-12"
      data-testid="marketing-avatar-grid-slot"
    >
      {layers.map((src, layer) => (
        <Face
          key={src}
          src={src}
          isOnTop={layer > 0}
          isLeaving={layer === 0 && !!incoming && arrived === incoming}
          onShown={
            layer > 0
              ? () => {
                  setArrived(src);
                  onArrived(src);
                }
              : undefined
          }
        />
      ))}
    </div>
  );
}

/**
 * The participant network field: a block of faces under the hero's claim.
 *
 * Faces are a fixed size, 36px on phones and tablets and 48px on a desktop,
 * and the field shows exactly as many as fill whole rows at the width it has:
 * five rows on a phone, three anywhere wider. Each fades in as its image
 * arrives. When there are more faces than places the rest take turns: one at a
 * time is laid over a random place and faded up once loaded, and the face it
 * covers goes back into the pool, so a place is never empty mid-change.
 *
 * The faces are decoration to a screen reader. They carry no names, and dozens
 * of unlabelled images would be dozens of interruptions, so the whole field is
 * hidden from the accessibility tree.
 */
export function MarketingAvatarGrid({
  photos,
  isLoading = false,
  isRotating = true,
  className,
}: Props) {
  // Held in state rather than a ref, so the width is measured when the field
  // actually appears: it is not on the page while the list is loading.
  const [field, setField] = useState<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<Layout>(DEFAULT_LAYOUT);
  const count = visibleCount(photos.length, layout);

  // Each place holds the face showing and, mid-change, the one arriving over it.
  const [places, setPlaces] = useState<string[][]>(() =>
    photos.slice(0, count).map((src) => [src])
  );
  const placesRef = useRef<string[][]>(places);
  const poolRef = useRef<string[]>(photos.slice(count));
  const settleRef = useRef<Map<string, () => void>>(new Map());

  useEffect(() => {
    if (!field) return;
    const update = () =>
      setLayout((prev) => {
        const next = measure(field);
        return next.perRow === prev.perRow && next.rows === prev.rows ? prev : next;
      });
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(field);
    return () => observer.disconnect();
  }, [field]);

  // A new list starts the field over. A new size keeps every face where it
  // is, whatever it was doing, and fills only the places that were added,
  // which fade in as their faces load; resetting them all at once swapped
  // every face in the field with no fade.
  const shownPhotos = useRef(photos);
  useEffect(() => {
    let next: string[][];
    if (shownPhotos.current !== photos) {
      shownPhotos.current = photos;
      next = photos.slice(0, count).map((src) => [src]);
    } else {
      const kept = placesRef.current.slice(0, count).map((layers) => [layers[0]]);
      const showing = new Set(kept.map(([src]) => src));
      const waiting = photos.filter((src) => !showing.has(src));
      next = [...kept, ...waiting.slice(0, count - kept.length).map((src) => [src])];
    }
    const showing = new Set(next.map(([src]) => src));
    placesRef.current = next;
    poolRef.current = photos.filter((src) => !showing.has(src));
    settleRef.current = new Map();
    setPlaces(next);
  }, [photos, count]);

  useEffect(() => {
    if (!isRotating || photos.length <= count || count === 0) return;

    const timeouts = new Set<ReturnType<typeof setTimeout>>();
    const later = (fn: () => void, ms: number) => {
      const id = setTimeout(() => {
        timeouts.delete(id);
        fn();
      }, ms);
      timeouts.add(id);
      return id;
    };
    const write = (next: string[][]) => {
      placesRef.current = next;
      setPlaces(next);
    };

    // The next face is chosen as the wait before it begins, and its image
    // asked for then, so it has usually arrived by the time its change is
    // due. Asked for only once the wait was over, a slow image added its
    // download to the wait, and the gap between changes ran past its longest.
    // It is out of the pool from the moment it is chosen, as it is for the
    // length of the change, so nothing else can pick it meanwhile.
    const upcoming = new Set<string>();
    // When a change last began, on either clock, so the other keeps its
    // distance.
    let lastStart = -Infinity;
    const choose = () => {
      const pool = poolRef.current;
      if (pool.length === 0) return undefined;
      const [src] = pool.splice(Math.floor(Math.random() * pool.length), 1);
      if (typeof Image !== "undefined") new Image().src = src;
      return src;
    };

    // `then` is called once the change is visibly under way, or once it has
    // been given up, and starts the wait before the next.
    const swap = (src: string, then: () => void) => {
      const free = placesRef.current
        .map((layers, index) => (layers.length === 1 ? index : -1))
        .filter((index) => index >= 0);
      if (free.length === 0) {
        poolRef.current.push(src);
        then();
        return;
      }

      const index = free[Math.floor(Math.random() * free.length)];
      // The covered face is not returned to the pool until it is really
      // gone, so it cannot be picked for a second place while it is still on
      // screen in the first.
      const outgoing = placesRef.current[index][0];
      write(placesRef.current.map((layers, i) => (i === index ? [outgoing, src] : layers)));

      // An image that never arrives gives its turn back rather than holding
      // the place half-changed.
      const giveUp = later(() => {
        settleRef.current.delete(src);
        poolRef.current.push(src);
        write(placesRef.current.map((layers, i) => (i === index ? [outgoing] : layers)));
        then();
      }, LOAD_PATIENCE_MS);

      // Once it has loaded, the face underneath fades out and the arriving
      // one fades up after it; when both halves are over, the face
      // underneath is let go. The arriving element stays mounted, so it does
      // not fade a second time.
      settleRef.current.set(src, () => {
        settleRef.current.delete(src);
        clearTimeout(giveUp);
        timeouts.delete(giveUp);
        then();
        later(() => {
          poolRef.current.push(outgoing);
          write(placesRef.current.map((layers, i) => (i === index ? [src] : layers)));
        }, FADE_MS + 50);
      });
    };

    // One clock: a change, a wait, the next change. A hidden tab draws
    // nothing, so a change made there would be found already done on coming
    // back, with no fade seen. None is started then. The wait is measured
    // from when one change is seen to begin to when the clock's next is, so
    // a face whose image took a moment does not bring the next change closer.
    const next = () => {
      const src = choose();
      if (src) upcoming.add(src);
      const begin = () => {
        const since = Date.now() - lastStart;
        if (since < SPACING_MS) {
          later(begin, SPACING_MS - since);
          return;
        }
        if (!src) {
          next();
          return;
        }
        upcoming.delete(src);
        if (typeof document !== "undefined" && document.hidden) {
          poolRef.current.push(src);
          next();
          return;
        }
        lastStart = Date.now();
        swap(src, () => {
          lastStart = Date.now();
          next();
        });
      };
      later(begin, WAIT_MS.shortest + Math.random() * (WAIT_MS.longest - WAIT_MS.shortest));
    };
    for (let clock = 0; clock < CLOCKS; clock++) next();
    return () => {
      timeouts.forEach(clearTimeout);
      // A face chosen for a change that will now not happen goes back.
      upcoming.forEach((src) => poolRef.current.push(src));
    };
  }, [isRotating, photos, count]);

  if (isLoading) {
    return (
      <div
        data-testid="marketing-avatar-grid-loading"
        // The height the field will have, so the cards below do not jump when
        // the faces arrive.
        className={`flex h-[228px] w-full items-center justify-center md:h-[132px] lg:h-[168px] ${
          className ?? ""
        }`}
      >
        <Loader innerClassName="h-14 w-14" />
      </div>
    );
  }

  if (photos.length === 0) return null;

  return (
    <div
      ref={setField}
      aria-hidden="true"
      data-testid="marketing-avatar-grid"
      // Clipped to the rows it is meant to have, so the paint before the width
      // has been measured cannot show a ragged extra row.
      className={`flex max-h-[228px] w-full flex-wrap justify-center gap-3 overflow-hidden md:max-h-[132px] lg:max-h-[168px] ${
        className ?? ""
      }`}
    >
      {places.map((layers, index) => (
        <Place
          key={index}
          layers={layers}
          onArrived={(src) => settleRef.current.get(src)?.()}
        />
      ))}
    </div>
  );
}

export default MarketingAvatarGrid;
